import { useTranslation } from 'react-i18next';
import {
    type InventoryFailure,
    hasInventoryDetail,
} from '../../../../api/inventory/inventoryErrors';

interface InventoryErrorPanelProps {
    failure: InventoryFailure | null;
    /**
     * Set when the station was created but its objects were not — the one
     * partial state the create sequence can leave behind.
     */
    orphanStationId?: string | null;
}

/**
 * What went wrong, in the terms the operator can act on.
 *
 * The endpoints return four distinct 4xx payloads with four distinct remedies,
 * and the whole point of the backend reporting every faulty entry at once is
 * lost if the UI prints one sentence. An operator entering 40 compartments sees
 * all their mistakes here, indexed, rather than one per round-trip.
 */
const InventoryErrorPanel = ({ failure, orphanStationId }: InventoryErrorPanelProps) => {
    const { t } = useTranslation();

    if (!failure && !orphanStationId) return null;

    return (
        <div className={'mt-5 rounded-lg border border-foreground/20 bg-foreground/5 px-4 py-3'}>
            {orphanStationId && (
                <p className={'text-sm text-foreground'}>
                    {t('station_create.errors.station_created_objects_failed', {
                        stationId: orphanStationId,
                    })}
                </p>
            )}

            {failure && (
                <div className={'mt-2 flex flex-col gap-3'}>
                    {/* No response at all is its own case: nothing was rejected,
                        nothing arrived. Retrying is right here and wrong for a
                        400, so they must not read the same. */}
                    {failure.unreachable ? (
                        <p className={'text-sm text-foreground'}>
                            {t('station_create.errors.unreachable')}
                        </p>
                    ) : (
                        <p className={'text-sm text-foreground'}>
                            {failure.message ?? t('station_create.errors.generic')}
                            {failure.status != null && ` (${failure.status})`}
                        </p>
                    )}

                    {/* Every bad entry, by position. */}
                    {failure.fieldErrors.length > 0 && (
                        <ul className={'flex list-disc flex-col gap-1 pl-5 text-sm text-foreground/80'}>
                            {failure.fieldErrors.map((fieldError) => (
                                <li key={`${fieldError.index}-${fieldError.field}-${fieldError.message}`}>
                                    {t('station_create.errors.object_row', {
                                        position: fieldError.index + 1,
                                        field: fieldError.field,
                                    })}
                                    {': '}
                                    {fieldError.message}
                                </li>
                            ))}
                        </ul>
                    )}

                    {failure.occupiedDoors.length > 0 && (
                        <p className={'text-sm text-foreground/80'}>
                            {t('station_create.errors.doors_taken', {
                                doors: failure.occupiedDoors.join(', '),
                            })}
                        </p>
                    )}

                    {/* Which objects, and which Product already holds them —
                        naming only the count would leave the operator unable to
                        act, and this is the case that used to shrink the first
                        Product's bookable pool silently. */}
                    {failure.conflicts.length > 0 && (
                        <div className={'flex flex-col gap-1'}>
                            <p className={'text-sm text-foreground/80'}>
                                {t('station_create.errors.link_conflicts')}
                            </p>
                            <ul className={'flex list-disc flex-col gap-1 pl-5 text-sm text-foreground/80'}>
                                {failure.conflicts.map((conflict) => (
                                    <li key={conflict.linkId}>
                                        {t('station_create.errors.link_conflict_row', {
                                            assetId: conflict.assetId,
                                            productId: conflict.currentProductId,
                                        })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {failure.blockers.length > 0 && (
                        <ul className={'flex list-disc flex-col gap-1 pl-5 text-sm text-foreground/80'}>
                            {failure.blockers.map((blocker) => (
                                <li key={blocker}>
                                    {t(`station_create.errors.blocker.${blocker}`, {
                                        defaultValue: blocker,
                                    })}
                                </li>
                            ))}
                        </ul>
                    )}

                    {failure.missing.length > 0 && (
                        <p className={'text-sm text-foreground/80'}>
                            {t('station_create.errors.missing_assets', {
                                ids: failure.missing.join(', '),
                            })}
                        </p>
                    )}

                    {/* A 4xx with no detail list and no message would otherwise
                        render as an empty box. */}
                    {!failure.unreachable && !failure.message && !hasInventoryDetail(failure) && (
                        <p className={'text-sm text-foreground/80'}>
                            {t('station_create.errors.generic')}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default InventoryErrorPanel;
